<script setup lang="ts">
import { CheckCircleIcon } from '@heroicons/vue/solid'
import { ExclamationCircleIcon } from '@heroicons/vue/outline'
import { computed, ConcreteComponent, ref, watch } from 'vue'
import { PersonalAccountData, usePersonalAccountDataState } from '../../../../utilities/account/useAccountDataPersonal'
import AccountKycDocuments from '../../common/verification/AccountKycDocuments.vue'
import { KycDocument } from '../../../../utilities/api'
import { LegalAccountData } from '../../../../utilities/account/useAccountDataLegal'
import BaseInputText from '../../../base/BaseInputText.vue'

export interface KycDocumentTab {
    header: string
    icon: ConcreteComponent
    accepted: boolean
    required: boolean
    documents: Array<KycDocument>
    show: boolean
    subheadline: string
}

export interface KycDocumentTabs {
    kyc_external?: KycDocumentTab
    kyc_id?: KycDocumentTab
    kyc_legal?: KycDocumentTab
    kyc_tax?: KycDocumentTab
    kyc_address?: KycDocumentTab
    depot?: KycDocumentTab
}

const props = defineProps<{
    kycDocumentTabs: KycDocumentTabs
    tabKey: keyof KycDocumentTabs
    accountStore: PersonalAccountData | LegalAccountData
}>()
const emit = defineEmits(['update:tabKey'])

const personalAccountStore = usePersonalAccountDataState()

const companyEmail = ref<string>('')
const role = ref<string>('')

const currentTabKey = ref(props.tabKey)
watch(props.tabKey, (val) => (currentTabKey.value = val))
watch(currentTabKey, (val) => emit('update:tabKey', val))

const currentTab = computed(() => props.kycDocumentTabs[currentTabKey.value])
const shownKycDocumentTabs = computed(() => {
    return Object.keys(props.kycDocumentTabs)
        .filter((key) => props.kycDocumentTabs[key].show)
        .reduce((cur, key) => {
            return Object.assign(cur, { [key]: props.kycDocumentTabs[key] })
        }, {})
})

function isCurrentTab(index) {
    return currentTabKey.value === index
}

function hasTabErrors(tab): boolean {
    return tab.required && !tab.accepted
}

const renderKycDocuments = computed(() => {
    return (
        currentTab.value.header !== 'Signteq' && currentTab.value.header !== 'IDNow' && currentTabKey.value !== 'depot'
    )
})
</script>

<template>
    <div>
        <div class="md:hidden">
            <label for="tabs" class="sr-only">Select a tab</label>
            <select
                id="tabs"
                name="tabs"
                class="block w-full rounded-md border-gray-300 focus:border-secondary focus:ring-secondary"
                @change="currentTabKey = $event.target.value">
                <option
                    v-for="(tab, idx) in shownKycDocumentTabs"
                    :key="tab.name"
                    :value="idx"
                    :selected="isCurrentTab(idx)">
                    {{ tab.header }}
                </option>
            </select>
        </div>
        <div class="hidden md:block">
            <div class="border-b border-gray-200">
                <nav class="-mb-px flex flex-wrap px-5" aria-label="Tabs">
                    <button
                        v-for="(tab, idx) in shownKycDocumentTabs"
                        :key="idx"
                        :class="[
                            isCurrentTab(idx)
                                ? 'border-secondary text-gray-900'
                                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                            'group mr-4 inline-flex items-center border-b-2 px-1 py-4 text-sm font-medium no-underline last:mr-0',
                        ]"
                        :aria-current="isCurrentTab(idx) ? 'page' : undefined"
                        @click="currentTabKey = idx">
                        <component
                            :is="tab.icon"
                            :class="[
                                isCurrentTab(idx) ? 'text-gray-900' : 'text-gray-400 group-hover:text-gray-500',
                                '-ml-0.5 mr-2 h-5 w-5',
                            ]"
                            aria-hidden="true" />
                        <span>{{ tab.header }}</span>
                        <CheckCircleIcon
                            :is="tab.icon"
                            v-if="tab.accepted"
                            v-tooltip="$t('verification.complete_verification_complete')"
                            class="-mr-0.5 ml-1.5 h-5 w-5 text-green-500"
                            aria-hidden="true" />
                        <ExclamationCircleIcon
                            v-else-if="hasTabErrors(tab)"
                            v-tooltip="$t('verification.complete_verification_outstanding')"
                            class="-mr-0.5 ml-1.5 h-5 w-5 text-red-700"
                            aria-hidden="true" />
                    </button>
                </nav>
            </div>
        </div>
    </div>

    <div class="m-6">
        <slot name="additional-header-content" />
        <h3 v-if="currentTabKey !== 'kyc_external'" class="mb-2 mt-6 text-lg font-medium leading-6 text-gray-900">
            {{ currentTab.headline ?? currentTab.header }}
        </h3>
        <p
            v-if="currentTab.subheadline"
            class="mb-3 text-sm font-medium text-gray-500"
            v-html="currentTab.subheadline"></p>
        <div
            v-if="(currentTab.header !== 'Signteq' || currentTab.header !== 'IDNow') && currentTabKey == 'kyc_legal'"
            class="grid h-auto w-full grid-cols-2 gap-4 py-6">
            <BaseInputText
                name="full_name"
                :label="$t('verification.full_name')"
                :model-value="
                    personalAccountStore.naturalProfile.first_name + ' ' + personalAccountStore.naturalProfile.last_name
                "
                readonly
                class="bg-gray-100" />
            <BaseInputText
                name="email"
                :label="$t('verification.email')"
                :model-value="personalAccountStore.naturalProfile.email"
                readonly
                class="bg-gray-100" />
        </div>
        <AccountKycDocuments
            v-if="renderKycDocuments"
            :key="currentTabKey"
            :kyc-documents="currentTab"
            :verification-type="currentTabKey"
            :store="accountStore"
            class="mb-6" />
    </div>

    <div class="m-6">
        <slot name="body" />
    </div>
</template>
